import React, { useEffect, useMemo, useState } from 'react';

import { DialogActions, DialogContent, TextField } from '@material-ui/core';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import AccessibleDialog from 'components/_dialogs/AccessibleDialog';
import ColoredButton from 'components/ColoredButton';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';

type Props = {
  deadline: string;
  instructions: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (payload: { deadline: string; reviewInstructions: string }) => void;
  open: boolean;
};

const EditRopaReviewModal: React.FC<Props> = ({ deadline, instructions, loading = false, onClose, onConfirm, open }) => {
  const { t } = useTranslation();
  const minDeadlineDate = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate());
    return format(tomorrow, 'yyyy-MM-dd');
  }, []);
  const [formDeadline, setFormDeadline] = useState(deadline);
  const [formInstructions, setFormInstructions] = useState(instructions);
  const [deadlineError, setDeadlineError] = useState('');
  const [instructionsError, setInstructionsError] = useState('');
  const deadlineFieldId = 'edit-ropa-review-deadline';
  const instructionsFieldId = 'edit-ropa-review-instructions';

  useEffect(() => {
    if (open) {
      setFormDeadline(deadline);
      setFormInstructions(instructions);
      setDeadlineError('');
      setInstructionsError('');
    }
  }, [deadline, instructions, open]);

  const handleClose = () => {
    setDeadlineError('');
    setInstructionsError('');
    onClose();
  };

  const handleConfirm = () => {
    let hasError = false;

    if (!formDeadline) {
      setDeadlineError(t(validation_messages.required));
      hasError = true;
    } else {
      const selectedDate = new Date(formDeadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate <= today) {
        setDeadlineError(t(processing_activities_messages.asset_ropa.modals.start_review.deadline_future));
        hasError = true;
      }
    }

    if (!formInstructions.trim()) {
      setInstructionsError(t(validation_messages.required));
      hasError = true;
    }

    if (hasError) return;

    onConfirm({
      deadline: formDeadline,
      reviewInstructions: formInstructions.trim(),
    });
  };

  return (
    <AccessibleDialog
      fullWidth
      maxWidth='sm'
      onClose={handleClose}
      open={open}
      title={t(processing_activities_messages.asset_ropa.modals.edit_review.title)}
    >
      <DialogContent>
        <TextField
          autoFocus
          error={Boolean(deadlineError)}
          fullWidth
          helperText={deadlineError}
          id={deadlineFieldId}
          InputLabelProps={{ htmlFor: deadlineFieldId, shrink: true }}
          inputProps={{ 'aria-label': t(processing_activities_messages.asset_ropa.modals.start_review.deadline), min: minDeadlineDate }}
          label={t(processing_activities_messages.asset_ropa.modals.start_review.deadline)}
          margin='normal'
          onChange={event => {
            setFormDeadline(event.target.value);
            if (deadlineError) setDeadlineError('');
          }}
          type='date'
          value={formDeadline}
          variant='outlined'
        />
        <TextField
          error={Boolean(instructionsError)}
          fullWidth
          helperText={instructionsError || t(processing_activities_messages.asset_ropa.modals.start_review.instructions_help)}
          id={instructionsFieldId}
          InputLabelProps={{ htmlFor: instructionsFieldId }}
          inputProps={{ 'aria-label': t(processing_activities_messages.asset_ropa.modals.start_review.instructions) }}
          label={t(processing_activities_messages.asset_ropa.modals.start_review.instructions)}
          margin='normal'
          multiline
          onChange={event => {
            setFormInstructions(event.target.value);
            if (instructionsError) setInstructionsError('');
          }}
          rows={5}
          value={formInstructions}
          variant='outlined'
        />
      </DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='grey'
          disabled={loading}
          icon={false}
          onClick={handleClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel)}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={loading}
          icon={false}
          onClick={handleConfirm}
          showLoader={loading}
          textLike={false}
          variant='contained'
        >
          {t(processing_activities_messages.asset_ropa.modals.edit_review.confirm)}
        </ColoredButton>
      </DialogActions>
    </AccessibleDialog>
  );
};

export default EditRopaReviewModal;
